/* eslint-disable @eslint-react/web-api-no-leaked-interval */
import { useCallback, useEffect, useRef, useState } from 'react'

interface UseTimerInterface {
  onTimeOver: () => void
  limitSeconds: number
}

interface UseTimerResult {
  timeLeft: () => number
  start: () => void
  reset: () => void
  restart: () => void
}

export const useTimer = (params: UseTimerInterface): UseTimerResult => {
  const time = useRef(params.limitSeconds)
  const interval = useRef<ReturnType<typeof setInterval>>()
  const [shouldStart, setShouldStart] = useState(false)
  const [restartIndex, setRestartIndex] = useState(0)

  const clearCurrentInterval = useCallback(() => {
    if (!interval.current) {
      return
    }

    clearInterval(interval.current)
    interval.current = undefined
  }, [])

  const reset = useCallback(() => {
    time.current = params.limitSeconds
    clearCurrentInterval()
    setShouldStart(false)
  }, [clearCurrentInterval, params.limitSeconds])

  const start = useCallback(() => setShouldStart(true), [])

  const timeLeft = useCallback(() => time.current, [])

  const restart = useCallback(() => {
    clearCurrentInterval()
    time.current = params.limitSeconds
    setRestartIndex(prev => prev + 1)
    setShouldStart(true)
  }, [clearCurrentInterval, params.limitSeconds])

  useEffect(() => {
    if (!shouldStart) {
      clearCurrentInterval()
      return
    }

    interval.current = setInterval(() => {
      if (time.current > 1) {
        time.current -= 1
        return
      }

      time.current = 0

      clearCurrentInterval()
      setShouldStart(false)
      params.onTimeOver()
    }, 1000)

    return (): void => {
      clearCurrentInterval()
    }
  }, [clearCurrentInterval, params.onTimeOver, restartIndex, shouldStart])

  useEffect(() => {
    time.current = params.limitSeconds
  }, [params.limitSeconds])

  return {
    timeLeft,
    start,
    reset,
    restart,
  }
}
